import moment from 'moment'
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import {
    Button,
    Checkbox,
    Breadcrumb,
    MessageBox,
    Pagination,
} from 'element-react'
import { tagHotjarRecording } from '../../helpers/util'
import { Section, MainSection } from '../../components/views/includes/Wrappers'
import Spacer from '../../components/views/includes/Spacer'
import CustomLoader from '../../components/user/includes/CustomLoader'
import useUsers from '../../hooks/useUsers'
import useAdmin from '../../hooks/useAdmin'
import AppRoutes from '../../routes/appRoutes'

const isActive = ({ created_at, updated_at }) => {
    if (!created_at || !updated_at) {
        return false
    }

    const creation = moment.utc(created_at)
    const update = moment.utc(updated_at)
    return update.isAfter(creation)
}

const firstLastEmailSorter = (a, b) => {
    const firstNameSort = ('' + a.first_name).localeCompare(b.first_name)
    const lastNameSort = ('' + a.last_name).localeCompare(b.last_name)
    const emailSort = ('' + a.email).localeCompare(b.email)
    return firstNameSort !== 0
        ? firstNameSort
        : lastNameSort !== 0
        ? lastNameSort
        : emailSort
}

// This page should only be accessible by admins (not Salespersons)
// so no auth checks are necessary on the page
const ManageAdminsPage = props => {
    const [page, setPage] = useState(1)
    const ADMINS_PER_PAGE = 20

    // Status should be one of ready, loading, success, or error
    const [deleteStatus, setDeleteStatus] = useState('ready')
    const [listStatus, setListStatus] = useState('ready')
    const [adminUsers, setAdminUsers] = useState([])
    const [error, setError] = useState('')
    const { getListAdmins } = useUsers()
    const { handleChangeSalesPersonStatus, handleDeleteAdmin } = useAdmin()

    const onLoadAdmin = response => {
        setListStatus('success')
        setAdminUsers(response.data.sort(firstLastEmailSorter))
    }

    const onErrorLoadAdmin = error => {
        tagHotjarRecording('API Error')
        let message = 'Admin users not found.'
        if (error.response.data) {
            message = error.response.data.message
        }
        setListStatus('error')
        setError(message)
    }

    const onAdminDeletedSuccess = response => {
        setDeleteStatus(response.status === 200 ? 'success' : 'error')
        // Going to the first page here prevents issues with deleting
        // the last admin on a page.
        setPage(1)
    }

    const onAdminDeletedError = error => {
        tagHotjarRecording('API Error')
        setDeleteStatus('error')
        console.error('User deletion failed: ' + error.message)
    }

    useEffect(() => {
        // Only get the list before any deletions or after a successful one,
        // not during a deletion or after a deletion failure (deletion is the
        // only way to change the list from this page)
        const initialRenderOrListHasChanged =
            deleteStatus === 'ready' || deleteStatus === 'success'
        if (!initialRenderOrListHasChanged) {
            return
        }

        setListStatus('loading')
        getListAdmins(onLoadAdmin, onErrorLoadAdmin)
    }, [deleteStatus])

    const setSalespersonStatus = (userId, isSalesperson) => {
        handleChangeSalesPersonStatus({ userId, isSalesperson })
    }

    const deleteAdmin = userId => {
        setDeleteStatus('loading')
        handleDeleteAdmin(userId, onAdminDeletedSuccess, onAdminDeletedError)
    }

    const deleteAdminPrompt = userId => {
        MessageBox.confirm(
            'Are you sure you want to delete this admin?',
            'Delete Admin?',
            {
                showCancelButton: true,
                confirmButtonText: 'Yes, Delete',
                cancelButtonText: 'No',
                customClass: 'small-prompt',
            },
        )
            .then(() => {
                deleteAdmin(userId)
            })
            .catch(() => {
                console.error('There was an error during user deletion.')
            })
    }

    const firstIndexInView = (page - 1) * ADMINS_PER_PAGE
    const adminsOnThisPage = adminUsers.slice(
        firstIndexInView,
        firstIndexInView + ADMINS_PER_PAGE,
    )
    const listOrMessage =
        listStatus === 'loading' ? (
            <CustomLoader text="Loading" />
        ) : listStatus === 'error' ? (
            <div>{error}</div>
        ) : (
            <ul className="table-list">
                {adminsOnThisPage.map(admin => (
                    <li className="admin-user" key={`row-${admin.id}`}>
                        <div className="row">
                            <div className="columns medium-9 position-relative">
                                <div className="content">
                                    <h5
                                        style={{
                                            display: 'inline-block',
                                        }}
                                    >
                                        {admin.first_name} {admin.last_name}
                                    </h5>
                                    {isActive(admin) ? null : (
                                        <span className="inline-block padding-left-tiny uppercase text-xxsmall font-bold dark-gray-color">
                                            <span className="alert-bg bullet" />
                                            Not Activated
                                        </span>
                                    )}
                                    <span className="secondary-color block truncate">
                                        {admin.email}
                                    </span>

                                    <Checkbox
                                        checked={admin.salesperson}
                                        onChange={() =>
                                            setSalespersonStatus(
                                                admin.id,
                                                !admin.salesperson,
                                            )
                                        }
                                        disabled={
                                            admin.email ===
                                            'help@liftonenow.com' /* Show that master admin can't be demoted. */
                                        }
                                    >
                                        Salesperson
                                    </Checkbox>

                                    <Spacer size="tiny" />
                                </div>
                            </div>
                            <div className="columns medium-3">
                                <div className="align-bottom text-right">
                                    {/* Remove the button for deleting the main admin. */}
                                    {admin.email ===
                                    'help@liftonenow.com' ? null : (
                                        <Button
                                            onClick={() =>
                                                deleteAdminPrompt(admin.id)
                                            }
                                            type="danger"
                                            icon="delete"
                                            size="small"
                                            disabled={
                                                deleteStatus === 'loading'
                                            }
                                        >
                                            {' '}
                                            Remove
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        )

    const areMoreThanFiveAdmins = adminUsers.length > 5
    const pagination = areMoreThanFiveAdmins ? (
        <div className="row">
            <div className="columns large-6 medium-6 small-12">
                <Pagination
                    layout="prev, pager, next"
                    onCurrentChange={page => setPage(page)}
                    pageSize={ADMINS_PER_PAGE}
                    currentPage={page}
                    total={adminUsers.length}
                    small={true}
                />
            </div>
            <div className="columns large-6 medium-6 small-12">
                <div className="text-right">
                    <small>{adminUsers.length} Results Found </small>
                </div>
            </div>
        </div>
    ) : (
        <Section>
            <div className="medium-text-right">
                <small>
                    {`${adminUsers.length} ${
                        adminUsers.length === 1 ? 'Result' : 'Results'
                    } Found`}
                </small>
            </div>
        </Section>
    )

    const {
        history: { goBack },
    } = props
    return (
        <MainSection className="admin--user-management">
            <div className="row">
                <div className="columns medium-9">
                    <Breadcrumb separator="/" className="margin-top-small">
                        <Breadcrumb.Item>
                            <Link to={AppRoutes.home()}>Home</Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>Admin Management</Breadcrumb.Item>
                    </Breadcrumb>
                </div>
                <div className="columns medium-3 show-for-medium">
                    <Button
                        type="text"
                        onClick={() => goBack()}
                        className="button-back margin-top-small"
                    >
                        <i className="icon-chevron-thin-left"></i> Go Back
                    </Button>
                </div>
            </div>

            <Spacer size="small" />

            <div className="row">
                <div className="large-8 small-8 columns">
                    <h4 className="margin-bottom-none">Admin Management</h4>
                </div>
            </div>

            <Section>{listOrMessage}</Section>

            {pagination}
        </MainSection>
    )
}

ManageAdminsPage.propTypes = {
    history: PropTypes.shape({
        goBack: PropTypes.func,
    }),
}

export default ManageAdminsPage
